import React from 'react';
import { motion } from 'framer-motion';
import { Award, Globe, ShieldCheck, Truck, ArrowRight, MessageSquare } from 'lucide-react';
import { generateGeneralWhatsAppLink } from '../data/products';

const DealershipSection = () => {
  const perks = [
    {
      icon: <Globe size={24} />,
      title: "EXCLUSIVE TERRITORY RIGHTS",
      description: "Secure exclusive country or regional distributorship rights for RAYYAN DENIM products."
    },
    {
      icon: <Award size={24} />,
      title: "GUARANTEED EXPORT QUALITY",
      description: "International selvedge denim with standard fits and different washes, developed for malls and multi-brand outlets (MBOs)."
    },
    {
      icon: <Truck size={24} />,
      title: "PRIORITY CONTAINER DISPATCH",
      description: "Dedicated export production lines ensuring on-time container shipments to designated international ports."
    },
    {
      icon: <ShieldCheck size={24} />,
      title: "BRAND & CATALOG SUPPORT",
      description: "Complete access to high-fashion editorial lookbooks, tech packs, fabric swatches, and POS materials."
    }
  ];

  return (
    <section style={{ padding: '90px 0', background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', color: '#ffffff', position: 'relative', overflow: 'hidden' }}>
      
      {/* Background Radial Glow */}
      <div
        style={{
          position: 'absolute',
          top: '-20%',
          right: '-10%',
          width: '500px',
          height: '500px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(224, 0, 135, 0.15) 0%, transparent 70%)',
          pointerEvents: 'none'
        }}
      />

      <div className="container" style={{ position: 'relative', zIndex: 2 }}>
        
        {/* Section Header */}
        <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto 60px' }}>
          <span className="section-badge" style={{ background: 'rgba(255, 255, 255, 0.12)', border: '1px solid rgba(255, 255, 255, 0.25)', color: '#ffffff' }}>
            <Award size={13} />
            INTERNATIONAL EXPANSION PROGRAM
          </span>

          <h2 style={{ fontSize: 'clamp(28px, 4.5vw, 50px)', fontFamily: 'var(--font-heading)', fontWeight: 900, textTransform: 'uppercase', color: '#ffffff', margin: '12px 0 16px', lineHeight: 1.15 }}>
            GLOBAL <span style={{ color: 'var(--accent-magenta)' }}>DEALERSHIP</span> & <span style={{ color: '#ffffff' }}>DISTRIBUTORSHIP</span>
          </h2>

          <p style={{ fontSize: 'clamp(15px, 1.8vw, 18px)', color: 'rgba(255, 255, 255, 0.8)', lineHeight: 1.6 }}>
            Partner with <span className="brand-name">RAYYAN DENIM</span> as an international dealer or country distributor. We work with apparel importers, malls, MBOs, regional retail chains, and overseas distributors seeking reliable denim collections.
          </p>
        </div>

        {/* 4 Key Benefits Grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: '28px',
            marginBottom: '56px'
          }}
        >
          {perks.map((perk, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
              whileHover={{ y: -6 }}
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                borderRadius: '16px',
                padding: '32px 24px',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px'
              }}
            >
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, var(--accent-purple), var(--accent-magenta))',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#ffffff',
                  boxShadow: '0 8px 20px rgba(224, 0, 135, 0.3)'
                }}
              >
                {perk.icon}
              </div>

              <h3 style={{ fontSize: '18px', fontFamily: 'var(--font-heading)', fontWeight: 800, color: '#ffffff', textTransform: 'uppercase', margin: 0 }}>
                {perk.title}
              </h3>

              <p style={{ fontSize: '13.5px', color: 'rgba(255, 255, 255, 0.72)', lineHeight: 1.6, margin: 0 }}>
                {perk.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* CTA Bar */}
        <div
          style={{
            background: 'rgba(255, 255, 255, 0.08)',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            borderRadius: '20px',
            padding: '36px 32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '24px'
          }}
        >
          <div>
            <h4 style={{ fontSize: '22px', fontFamily: 'var(--font-heading)', fontWeight: 900, color: '#ffffff', margin: '0 0 6px' }}>
              READY TO BECOME AN OFFICIAL INTERNATIONAL DISTRIBUTOR?
            </h4>
            <p style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.75)', margin: 0 }}>
              Discuss country territory rights, minimum order thresholds, and official dealership agreements directly with our Global Director.
            </p>
          </div>

          <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
            <a
              href={generateGeneralWhatsAppLink("International Dealership & Distributorship Application")}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-whatsapp"
              style={{ padding: '15px 28px', fontSize: '13px' }}
            >
              <MessageSquare size={18} />
              APPLY VIA WHATSAPP
            </a>

            <a
              href="/contact"
              className="btn-primary"
              style={{ padding: '15px 28px', fontSize: '13px', background: '#ffffff', color: '#0f172a' }}
            >
              <span>SUBMIT DEALERSHIP ENQUIRY</span>
              <ArrowRight size={16} />
            </a>
          </div>
        </div>

      </div>
    </section>
  );
};

export default DealershipSection;
